import { Injectable } from '@angular/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Observable } from 'rxjs';

export interface CameraError {
  code: string;
  message: string;
  type: 'PERMISSION_DENIED' | 'NOT_FOUND' | 'NOT_READABLE' | 'NOT_SUPPORTED' | 'NOT_SECURE_CONTEXT' | 'UNKNOWN';
}

@Injectable({ providedIn: 'root' })
export class CameraService {

  constructor() {}

  /**
   * Check if camera is supported and permissions are granted
   */
  async checkCameraSupport(): Promise<boolean> {
    // Check if running in native app
    if (Capacitor.isNativePlatform()) {
      try {
        const permissions = await Camera.checkPermissions();
        if (permissions.camera !== 'granted') {
          const result = await Camera.requestPermissions();
          return result.camera === 'granted';
        }
        return true;
      } catch (error) {
        console.error('Camera permission error:', error);
        return false;
      }
    }
    
    // Fallback for web
    return this.isWebCameraAvailable();
  }

  /**
   * Get camera stream for web or native
   */
  async getCameraStream(constraints?: MediaStreamConstraints): Promise<MediaStream> {
    if (!this.isWebCameraAvailable()) {
      throw this.createCameraError('NOT_SUPPORTED', 'Camera API not available');
    }

    if (!window.isSecureContext) {
      throw this.createCameraError('NOT_SECURE_CONTEXT', 'Kamera wymaga połączenia HTTPS. Otwórz stronę przez https://');
    }

    const defaultConstraints: MediaStreamConstraints = {
      video: { facingMode: { ideal: 'environment' } }
    };

    try {
      return await this.getCameraStreamFromProvider({ ...defaultConstraints, ...constraints });
    } catch (err: any) {
      throw this.mapMediaError(err);
    }
  }

  /**
   * Take a photo using native camera (Capacitor) or web fallback
   */
  async takePhoto(): Promise<string> {
    if (Capacitor.isNativePlatform()) {
      try {
        const image = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Camera
        });
        return image.dataUrl || '';
      } catch (error: any) {
        throw this.createCameraError('UNKNOWN', error.message || 'Failed to take photo');
      }
    } else {
      throw this.createCameraError('NOT_SUPPORTED', 'Photo capture requires native platform or use getCameraStream for web');
    }
  }

  /**
   * Check if running in iOS WKWebView
   */
  isIOSWKWebView(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isWKWebView = !('standalone' in window.navigator) && 
                        !/safari/.test(userAgent) && 
                        isIOS;
    return isWKWebView;
  }

  /**
   * Check if web camera API is available
   */
  isWebCameraAvailable(): boolean {
    return !!navigator.mediaDevices?.getUserMedia;
  }

  /**
   * Check if running on native platform
   */
  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get detailed camera support information
   */
  getCameraSupportInfo(): {
    isNative: boolean;
    isWeb: boolean;
    isSecure: boolean;
    isIOSWebView: boolean;
    isSupported: boolean;
  } {
    return {
      isNative: this.isNativePlatform(),
      isWeb: this.isWebCameraAvailable(),
      isSecure: window.isSecureContext,
      isIOSWebView: this.isIOSWKWebView(),
      isSupported: this.isNativePlatform() || (this.isWebCameraAvailable() && window.isSecureContext)
    };
  }

  /**
   * Get camera stream from the provider (wrapper for getUserMedia)
   * This method can be easily replaced with a different provider
   */
  private async getCameraStreamFromProvider(constraints: MediaStreamConstraints): Promise<MediaStream> {
    return await navigator.mediaDevices.getUserMedia(constraints);
  }

  /**
   * Create a standardized camera error
   */
  private createCameraError(type: CameraError['type'], message: string): CameraError {
    return {
      code: type,
      message,
      type
    };
  }

  /**
   * Map native media errors to our error interface
   */
  private mapMediaError(error: any): CameraError {
    let type: CameraError['type'] = 'UNKNOWN';
    let message = 'Nie można uruchomić kamery';

    if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
      type = 'PERMISSION_DENIED';
      message = 'Brak uprawnień do kamery. Zezwól na dostęp do kamery w ustawieniach przeglądarki.';
    } else if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') {
      type = 'NOT_FOUND';
      message = 'Nie znaleziono kamery. Podłącz kamerę i spróbuj ponownie.';
    } else if (error?.name === 'NotReadableError' || error?.name === 'TrackStartError') {
      type = 'NOT_READABLE';
      message = 'Kamera jest zajęta przez inną aplikację.';
    } else if (error?.message) {
      message = `Nie można uruchomić kamery: ${error.message}`;
    }

    return {
      code: type,
      message,
      type
    };
  }
}
